import { Permission, permissionSet } from '@all-hands-hq/plex-graphql-schema';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import React from 'react';

import { ReactComponent as AddAnswerSvg } from '../../../../assets/add_answer.svg';
import { AfterUpsertAnswerAction, FullAnswerFragment, QuestionStatus } from '../../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { Entities } from '../../../shared/slate/entities';
import { SlateEditor, SlateEditorValue } from '../../../shared/slate/SlateEditor';
import { QuestionHeader } from '../../feed/QuestionHeader';
import { canChangeAnswerApprovalStatus } from '../../permissions';
import { SplitButtonMenu, SplitButtonOption } from '../../shared/SplitButtonMenu';
import { DialogActions } from '../DialogActions';
import { DialogTopBar } from '../DialogTopBar';

export type FactAnswerInput = {
  body: SlateEditorValue;
  primaryLinkId: string | null | undefined;
  entities: Entities;
};

type Props = {
  mode: 'create' | 'update';
  isApprovedAnswer: boolean;
  question: FullAnswerFragment['question'];
  value: FactAnswerInput;
  onChange: (input: FactAnswerInput) => void;
  actionButtonOnClick: (afterAction: AfterUpsertAnswerAction) => void;
  actionButtonDisabled: boolean;
};

export function FactAnswerDialogContent({
  question,
  isApprovedAnswer,
  actionButtonOnClick,
  actionButtonDisabled,
  mode,
  value,
  onChange,
}: Props): React.ReactElement {
  const classes = useStyles();

  const plexUserRelation = question.plex.currentUserRelation;
  const plexRelation = plexUserRelation.state;

  const handleBodyChange = (body: SlateEditorValue): void => {
    onChange({
      ...value,
      body,
    });
  };

  const handleChangePrimaryLink = (primaryLinkId: string): void => {
    onChange({
      ...value,
      primaryLinkId,
    });
  };

  const menuOptions: Array<SplitButtonOption> = [
    ...(question.status !== QuestionStatus.Draft &&
    (question.status !== QuestionStatus.Published || !isApprovedAnswer) &&
    canChangeAnswerApprovalStatus({ plexRelation }) &&
    permissionSet(plexRelation).has(Permission.CHANGE_QUESTION_PUBLISH_STATUS)
      ? [
          {
            label: 'Publish Answer',
            onClick: (): void => actionButtonOnClick(AfterUpsertAnswerAction.ApproveAndPublish),
          },
        ]
      : []),
    {
      label: mode === 'create' ? 'Add Suggested Answer' : 'Update Answer',
      onClick: (): void => actionButtonOnClick(AfterUpsertAnswerAction.None),
    },
  ];

  return (
    <div className={classes.root}>
      <DialogTopBar>
        <PlexTypography size={TextSizesEnum.XXLarge} className={classes.rootTitle}>
          <AddAnswerSvg className={classes.rootTitleIcon} /> {mode === 'create' ? 'Add Answer' : 'Update Answer'}
        </PlexTypography>
      </DialogTopBar>

      <div className={classes.question}>
        <QuestionHeader
          plexName={question.plex.name}
          plexColor={question.plex.color}
          publishStatus={question.status}
          publishedDate={question.createdAt}
          authorName={question.author?.name || 'Root'}
          authorAvatar={question.author?.picture}
          authorTitle={question.author?.jobTitle}
          showUnreadBadge={false}
          hasAnswer={!!question.acceptedAnswerId}
        />
        <PlexTypography size={TextSizesEnum.XLarge} className={classes.questionText} text={question.text} />
      </div>

      <SlateEditor
        value={value.body}
        initialEntities={value.entities}
        onChange={handleBodyChange}
        title={'Type your answer here'}
        primaryLink={value.primaryLinkId}
        onChangePrimaryLink={handleChangePrimaryLink}
      />

      <DialogActions
        cta={
          <SplitButtonMenu
            options={menuOptions}
            menuProps={{
              marginThreshold: 0,
              anchorOrigin: { horizontal: 'right', vertical: 'top' },
              transformOrigin: { horizontal: 'right', vertical: 'bottom' },
            }}
            variant="contained"
            color="primary"
            disabled={actionButtonDisabled}
          />
        }
      />
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    rootTitle: {
      fontWeight: 'bold',
      marginLeft: spacings.xlarge,
      marginTop: spacings.normal,
      marginBottom: spacings.normal,
      color: colorPalette.dark._800,
    },
    rootTitleIcon: {
      width: 32,
      height: 32,
      marginRight: spacings.normal,
      fill: `${colorPalette.purpleGray._400} !important`, // sets the svg color
    },
    question: {
      borderTop: `1px solid ${colorPalette.gray._300}`,
      borderBottom: `1px solid ${colorPalette.gray._300}`,

      paddingRight: spacings.xlarge,
    },
    questionText: {
      fontWeight: 'bold',
      color: colorPalette.dark._800,

      paddingLeft: spacings.xlarge,
      paddingBottom: spacings.normal,
    },
  })
);
